
"use client"
import { getLanguageFromPathname, getLocalizedRouteSegment, withLanguagePrefix } from "@/app/lib/i18n-routing";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { motion } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";

type FeatureListItem = {
    excerpt?: string;
    localizedSlug?: string;
    slug: string;
    title: string;
};

type FeatureListProps = {
    data?: {
        description?: string;
        features?: FeatureListItem[];
        title?: string;
        titleHighlight?: string;
    };
};

export default function FeatureList(data: FeatureListProps) {
    const pathname = usePathname();

    if (!data || !data.data || !data.data.features || data.data.features.length === 0) {
        return null;
    }

    const items = data.data.features;
    const currentLang = getLanguageFromPathname(pathname);
    const sectionSegment = getLocalizedRouteSegment("features", currentLang);
    const title = data.data.title ?? "";
    const highlight = data.data.titleHighlight?.trim();
    const highlightIndex = highlight
        ? title.toLowerCase().indexOf(highlight.toLowerCase())
        : -1;
    
    
    // console.log(data, "feature data");
    // const icons = [Map, Zap, FileText, BarChart3, Users, Globe, Smartphone, Shield];
    // const items = [
    //     { "title": "Interactive Maps", "description": "Digital site layouts with real-time plot availability and visual exploration." },
    //     { "title": "Smart Booking Engine", "description": "Streamlined booking process with automated confirmations and payment tracking." },
    //     { "title": "Document Vault", "description": "Secure digital storage for all property documents with easy sharing capabilities." },
    //     { "title": "Analytics & Reports", "description": "Comprehensive dashboards with sales metrics, revenue tracking, and market insights." },
    //     { "title": "CRM Integration", "description": "Built-in customer relationship management for leads, follow-ups, and communications." },
    //     { "title": "Multi-Language Support", "description": "Reach a global audience with support for multiple languages." },
    //     { "title": "Mobile Responsive", "description": "Access everything on the go with a fully responsive mobile experience." },
    //     { "title": "Secure Payments", "description": "Safe and transparent payment processing with automated receipts." }
    // ]
    return (
        <>
            <section className="relative min-lg:h-[50vh] flex flex-col items-center justify-center">
                <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[500px] pointer-events-none top-0 w-full">
                    <div className='shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40'></div>
                    <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
                </div>
                <div className="container mx-auto relative z-10 pt-32 pb-4 md:pb-6 lg:pb-12">
                    <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto text-center">
                        <motion.h1 variants={fadeUp} className="banner-title">
                            {highlightIndex >= 0 && highlight ? (
                                <>
                                    {title.slice(0, highlightIndex)}
                                    <span className="banner-highlight-title">
                                        {title.slice(highlightIndex, highlightIndex + highlight.length)}
                                    </span>
                                    {title.slice(highlightIndex + highlight.length)}
                                </>
                            ) : (
                                title
                            )}
                        </motion.h1>
                        <motion.p variants={fadeUp} className="mt-3 banner-description">{data.data.description}</motion.p>
                    </motion.div>
                </div>
            </section>

            <section className="py-4 md:py-6 lg:py-8">
                <div className="container mx-auto">
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6 mx-auto max-w-6xl">
                        {items.map((item, i) => {
                            const routeSlug = item.slug  ?? item.localizedSlug;
                            return (
                                <Link href={withLanguagePrefix(`/${sectionSegment}/${routeSlug}`, currentLang)} key={i} title={item.title} className="flex " >
                                <motion.div key={i} variants={fadeUp} className="relative overflow-hidden group rounded-2xl p-6 bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-xl lg:border-r lg:border-white/10 flex-grow">
                                    <div className='absolute top-0 right-0 group-hover:w-full group-hover:h-full  w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl -mr-10 -mt-10 transition-all group-hover:bg-cyan-500/15'></div>
                                    {/* <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5 border border-cyan-500/20 group-hover:bg-cyan-500/20 transition-colors">
                                        <Icon className="w-6 h-6 text-white" />
                                    </div> */}
                                    <h2 className="text-lg lg:text-xl font-bold text-white leading-none">{item.title}</h2>
                                    <p className="mt-2 text-xs md:text-sm text-white/70 font-medium">{item.excerpt}</p>
                                </motion.div>
                                </Link>
                            );
                        })}
                    </motion.div>
                </div>
            </section>
        </>
    );
}
